import React from "react";
import { Save, RefreshCw, Copy } from "lucide-react";
import { Handle, Position, NodeProps } from "reactflow";
import Confirmation from "@components/common/ConfirmDialog";
import { EXCLUDE_KEYS, handlerStyle } from "../common/commonData";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";
import { toast } from "@components/ui/use-toast";

interface ExportedMeshflowNodeData {
  header: string;
  label?: string;
  configData?: any;
  onDelete?: (id: string) => void;
  onRefresh?: (flowId: string, nodeId: string) => void;
  borderColor?: string;
  shadowColor?: string;
  iconStyle?: string;
  isExportedMeshflow?: boolean;
  exportedFlowData?: any;
  // Execution-related properties
  executionStatus?: "pending" | "running" | "success" | "error";
  executionResult?: any;
  response?: any;
  outputData?: any;
  inputData?: any;
  error?: string;
  // State tracking
  isDeleted?: boolean;
  lastUpdated?: number;
}

const ExportedMeshflowNode: React.FC<NodeProps<ExportedMeshflowNodeData>> = ({
  id,
  data,
}) => {
  const [isDelete, setIsDelete] = React.useState(false);
  const [deleteNodeId, setDeleteNodeId] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState(false);
  const showLoadingBorder =
    !expanded &&
    (data.executionStatus === "running" || data.executionStatus === "pending");
  const loadingBorderColor = data?.borderColor || "var(--node-export)";

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Handle refresh for exported meshflows
  const handleRefresh = () => {
    if (data.onRefresh && data.exportedFlowData?.uid) {
      data.onRefresh(data.exportedFlowData.uid, id);
    }
  };

  // Prepare display data for InputViewer - mask encrypted fields
  const displayConfigData = React.useMemo(() => {
    if (!data.configData) return {};

    // Get encrypted field keys from isDecript array
    const encryptedFields = data.configData?.isDecript || [];

    // Fields to exclude from display (metadata and hardcoded fields)
    const excludeFields = [
      ...EXCLUDE_KEYS,
      "uid",
      "title",
      "description",
      "type",
      "workflow_type",
      "version",
      "node_count",
      "edge_count",
      "type_workflow",
    ];

    const displayData: any = {};
    Object.entries(data.configData)
      .filter(([key]) => !excludeFields.includes(key))
      .forEach(([key, value]) => {
        // Mask encrypted fields
        if (Array.isArray(encryptedFields) && encryptedFields.includes(key)) {
          displayData[key] = "********************";
        } else {
          displayData[key] = value;
        }
      });

    return displayData;
  }, [data.configData, data.lastUpdated]);

  // Check if configuration was deleted
  const isDeleted = data.isDeleted === true;

  // Handle delete click
  const handleDelete = (id: string) => {
    setIsDelete(true);
    setDeleteNodeId(id);
  };

  const confirmDelete = () => {
    if (deleteNodeId && data.onDelete) {
      data.onDelete(deleteNodeId);
    }
    setIsDelete(false);
    setDeleteNodeId(null);
  };

  return (
    <div
      style={{
        border: showLoadingBorder
          ? "1px solid var(--muted)"
          : `1px solid ${data?.borderColor || "var(--node-export)"}`,
        boxShadow: showLoadingBorder
          ? "none"
          : `0 1px 15px 1px ${data?.shadowColor || "rgba(59, 130, 246, 0.3)"}`,
        ...(showLoadingBorder
          ? ({
              "--exported-loading-color": loadingBorderColor,
            } as React.CSSProperties)
          : {}),
      }}
      className={`relative bg-background border shadow-lg rounded-2xl p-2 text-center font-semibold transition-all ${
        expanded
          ? "min-w-60 max-w-[480px] p-4"
          : "w-40 h-28 p-6 max-w-80 flex items-center justify-center"
      }`}
      onContextMenu={handleContextMenu}
    >
      {showLoadingBorder && (
        <>
          <style>
            {`
              @keyframes exported-node-spin-border {
                to {
                  transform: rotate(360deg);
                }
              }

              .exported-node-loading-border {
                position: absolute;
                inset: -1px;
                border-radius: inherit;
                overflow: hidden;
                pointer-events: none;
                z-index: 0;
              }

              .exported-node-loading-border::before {
                content: '';
                position: absolute;
                inset: -50%;
                background: conic-gradient(
                  from 0deg,
                  transparent 0deg,
                  transparent 300deg,
                  var(--exported-loading-color, var(--primary)) 340deg,
                  var(--exported-loading-color, var(--primary)) 360deg
                );
                animation: exported-node-spin-border 2s linear infinite;
              }

              .exported-node-loading-border::after {
                content: '';
                position: absolute;
                inset: 2px;
                background: var(--background);
                border-radius: inherit;
              }
            `}
          </style>
          <div className="exported-node-loading-border" />
        </>
      )}

      <Confirmation
        text={"Are you sure you want to delete this node?"}
        confirmAction={confirmDelete}
        rejectAction={() => setIsDelete(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={isDelete}
        closeDialog={() => setIsDelete(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(expanded, () =>
            setExpanded((prev) => !prev),
          ),
          // Add refresh button for exported meshflows
          {
            icon: <RefreshCw size={15} />,
            label: "Refresh Flow Data",
            onClick: handleRefresh,
            className: "text-primaryBlue",
            show: !!data.onRefresh,
          },
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      <div
        className={`relative z-10 flex items-center justify-between gap-4 rounded-md transition-all duration-500 ${
          expanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            <Save
              className={`${
                data.executionStatus === "running" ||
                data.executionStatus === "pending"
                  ? "animate-pulse"
                  : ""
              } ${data.iconStyle || "text-primaryBlue"}`}
            />
          }
          label={data.label || "Exported MeshFlow"}
          title={data.header}
          iconSize={35}
          borderColor={data?.borderColor || "var(--node-export)"}
          onToggle={() => setExpanded((prev) => !prev)}
          isExpanded={expanded}
          executionStatus={data.executionStatus}
        />
      </div>

      {/* Configuration data display */}
      {expanded && (
        <div className="mt-3 pt-3 border-t w-[400px]">
          {isDeleted ? (
            <div className="p-4 rounded-md bg-muted/20 border border-dashed border-muted-foreground/50">
              <div className="text-xs text-muted-foreground text-center">
                Exported MeshFlow data not available
              </div>
              <div className="text-[10px] text-muted-foreground/70 text-center mt-1">
                Node preserved in flow. Please re-export to restore.
              </div>
            </div>
          ) : Object.keys(displayConfigData).length > 0 ? (
            <>
              <div className="text-xs pb-2 text-muted-foreground ">
                {data.header} : execution result
              </div>

              {data.executionStatus == "pending" ||
              data.executionStatus == "running" ? (
                <div className="p-3 rounded-md bg-muted/20 border border-border max-h-60 overflow-y-auto">
                  loading execution result...
                </div>
              ) : (
                <div className="p-3 rounded-md bg-muted/20 border border-border max-h-60 overflow-y-auto">
                  <div className="space-y-2">
                    {Object.entries(displayConfigData).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-start gap-2 pb-2 border-b border-border/50 last:border-0"
                      >
                        <span className="text-xs font-semibold text-primary break-all">
                          {key}
                        </span>
                        <span className="text-xs text-muted-foreground">:</span>
                        <span className="text-xs flex justify-start text-primaryBlue break-all font-mono flex-1">
                          {typeof value === "string"
                            ? value
                            : typeof value === "object"
                              ? JSON.stringify(value, null, 2)
                              : String(value)}
                        </span>

                        <span className="ml-2">
                          <button
                            onClick={() => {
                              const copyValue =
                                typeof value === "string"
                                  ? value
                                  : typeof value === "object"
                                    ? JSON.stringify(value, null, 2)
                                    : String(value);
                              navigator.clipboard.writeText(copyValue);
                              toast({
                                variant: "info",
                                title: "Copied to clipboard",
                                description: `${key} value has been copied`,
                              });
                            }}
                            className="text-muted-foreground hover:text-primary transition-colors"
                            title={`Copy ${key} value to clipboard`}
                          >
                            <Copy size={14} />
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-4 rounded-md bg-muted/20 border border-dashed border-muted-foreground/50">
              <div className="text-xs text-muted-foreground text-center">
                No execution data available
              </div>
              <div className="text-[10px] text-muted-foreground/70 text-center mt-1">
                Right-click and{" "}
                <kbd className="text-primary ml-1"> Refresh </kbd> to execute
                the workflow and fetch updated data.
              </div>
            </div>
          )}

          {/* Show error if available */}
          {data.error && (
            <div className="mt-3 pt-3 border-t">
              <div className="text-xs pb-2 text-destructive">Error</div>
              <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30">
                <div className="text-xs text-destructive">{data.error}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Left handle - Input */}
      <Handle
        id="input"
        type="target"
        position={Position.Left}
        className={handlerStyle}
      />

      {/* Right handle - Output */}
      <Handle
        type="source"
        position={Position.Right}
        className={handlerStyle}
      />
    </div>
  );
};

export default ExportedMeshflowNode;
